import { db } from '@/core/db/dexie'
import { sessionStore } from '@/core/auth/SessionStore'
import type { LinhaMveOut, OutParteDiaria } from '@/core/db/entities'
import { pushParteDiariaOutbox } from '@/core/sync/parteDiariaSync'
import type { PushSummary } from '@/core/sync/carreteiroSync'
import { triggerForegroundSync } from '@/core/sync/foregroundSync'
import { requestBgSync } from '@/core/sync/registerBgSync'
import { upsertDraft } from './draftParteDiariaRepo'
import {
  errosDoLancamentoMve,
  errosEpp,
  horimetroInicial,
  iniciosDasLinhas,
  migrarEstado,
  modoDia,
  papelDaLinha,
  previaValorEpp,
  type ParteDiariaState,
} from './parteDiariaStore'
import { normalizarChapa } from './parteDiariaRegras'

/**
 * Persiste a parte diária no outbox (uma linha = um POST) e dispara push
 * imediato. O estado completo vai em `draftState` para reabrir se o servidor
 * recusar com algo corrigível.
 */
export async function saveParteDiaria(state: ParteDiariaState): Promise<{ pushResult: PushSummary | null }> {
  const user = sessionStore.current()
  if (!user) throw new Error('Sem sessão de usuário')
  if (state.mveId == null || state.modulo == null || state.mveTipo == null) {
    throw new Error('Parte diária incompleta — escolha o equipamento')
  }

  const base = {
    id: state.id,
    modulo: state.modulo,
    mveId: state.mveId,
    mvePrefixo: state.mvePrefixo,
    mveTipo: state.mveTipo,
    dt: state.dt,
    capturedAt: new Date().toISOString(),
    cadastradorNome: user.userNome,
    syncStatus: 'PENDING' as const,
    failureReason: null,
    rejeicao: null,
    draftState: JSON.stringify(migrarEstado({ ...state, rejeicao: null })),
  }

  let item: OutParteDiaria
  if (state.modulo === 'MVE') {
    if (state.linhas.length === 0) throw new Error('Parte diária sem linhas')
    const erro = errosDoLancamentoMve(state)[0]
    if (erro) throw new Error(`Linha ${erro.indice + 1}: ${erro.erros[0]?.mensagem ?? 'dados inválidos'}`)
    const inicios = iniciosDasLinhas(state)
    const linhas: LinhaMveOut[] = state.linhas.map((l, i) => ({
      id: l.id,
      papel: papelDaLinha(state, i),
      obraId: l.obraId!,
      obraNome: l.obraNome,
      servicoExecutadoId: l.servico!.id,
      servicoNome: l.servico!.nome,
      equipeId: l.equipeId,
      equipeNome: l.equipeNome,
      ficha: l.ficha?.trim() || null,
      medicaoInicio: inicios[i] ?? null,
      medicaoTermino: l.medicaoTermino!,
      chapa: normalizarChapa(l.chapa),
      hInicio: l.hInicio!,
      hFim: l.hFim!,
      observacao: l.observacao?.trim() || null,
    }))
    item = { ...base, mve: { modo: modoDia(state), horimetroInicialEsperado: horimetroInicial(state), linhas } }
  } else {
    const erro = errosEpp(state)[0]
    if (erro) throw new Error(erro.mensagem)
    item = {
      ...base,
      epp: {
        obraId: state.epp.obraId!,
        obraNome: state.epp.obraNome,
        servicoExecutadoId: state.epp.servico!.id,
        servicoNome: state.epp.servico!.nome,
        dtInicial: state.epp.dtInicial!,
        dtFinal: state.epp.dtFinal!,
        observacao: state.epp.observacao?.trim() || null,
        valorPrevia: previaValorEpp(state)?.valor ?? null,
      },
    }
  }

  await db.out_parte_diaria.put(item)
  void requestBgSync('outbox-data')

  let pushResult: PushSummary | null = null
  try {
    pushResult = await pushParteDiariaOutbox()
  } catch (e) {
    console.warn('[parteDiariaRepo] push falhou; mantendo no outbox:', e)
  }
  void triggerForegroundSync('post-save-parte-diaria', { ignoreCooldown: true })
  return { pushResult }
}

/** Recusada → volta a ser rascunho com a recusa anexada. null se não tem estado guardado. */
export async function reabrirParteRecusada(id: string): Promise<ParteDiariaState | null> {
  const row = await db.out_parte_diaria.get(id)
  if (!row?.draftState) return null
  let state: ParteDiariaState
  try {
    state = { ...migrarEstado(JSON.parse(row.draftState)), rejeicao: row.rejeicao ?? null }
  } catch {
    return null
  }
  await db.transaction('rw', db.out_parte_diaria, db.draft_parte_diaria, async () => {
    await upsertDraft(state)
    await db.out_parte_diaria.delete(id)
  })
  return state
}

export async function descartarParteOutbox(id: string): Promise<void> {
  await db.out_parte_diaria.delete(id)
}
